import * as React from "react";

import configStore from "../../stores/ConfigStore";
import contextStore from "../../stores/ContextStore";
import navigationStore from "../../stores/NavigationStore";
import panelStore from "../../stores/PanelStore";

import { createStyles, Theme, withStyles } from "@material-ui/core";

import {
    AddonActivatedMessage,
    AddonInactivatedMessage,
    AddonMessage,
    AddonMode,
    AppTheme,
    ChromeState,
    HostChangedMessage,
    MessageType,
    ParticipantInfo,
    PredefinedMeetingState,
    BadgeUpdateMessage,
    HostShellRequestMessage,
    KeyDownMessage,
} from "meet-addons-sdk";

import { observe } from "mobx";
import { inject, observer } from "mobx-react";
import { AddonInactiveMode } from "../../domain/api/addons/AddonInactiveMode";
import { AddonRuntimeInfo } from "../../domain/api/addons/AddonRuntimeInfo";
import { UserSummaryInfo } from "../../services/auth/UserSummaryInfo";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { INavigationProps } from "../../stores/INavigationProps";
import { INavigationStoreProps } from "../../stores/INavigationStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import addonMessanger from "./AddonMessanger";
import PredefinedRoute from "../enums/PredefinedRoute";

const styles = (theme: Theme) => createStyles({
    addon: {
        borderColor:  theme.palette.divider,
        borderStyle: "inset",
        borderWidth: 0,
        width: "100%",
    },
    mini: {
        borderColor:  theme.palette.divider,
        borderStyle: "inset",
        height: "auto",
        position: "absolute",
        width: "auto",
    },
});

export interface IAddonLoaderProps {
    addonConfig: AddonRuntimeInfo;
    offset: number;
    classes: {
        addon: string;
        mini: string;
    };
}

export interface IAddonLoaderState {
    right: number;
    bottom: number;
}

@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.NAVIGATION)
@observer
class AddonLoader extends React.Component<INavigationProps &
                                          IAddonLoaderProps &
                                          IContextStoreProps &
                                          IConfigStoreProps  &
                                          INavigationStoreProps, IAddonLoaderState> {

    private frameId: string;
    private mode!: AddonMode;
    private dirtyMode: boolean = false;

    private loaded = false;
    private delayedMessages: string[] = [];

    /**
     * Creates an instance of AddonLoader.
     * @param {RouteComponentProps} props
     * @memberof AddonLoader
     */
    constructor(props: INavigationProps &
                       IAddonLoaderProps &
                       IContextStoreProps &
                       IConfigStoreProps &
                       INavigationStoreProps) {
        super(props);

        this.frameId = `addon-${this.props.addonConfig.identifier}`;

        let bottom = 16;
        let right = 16;
        const rightCacheKey = `meet-addon-${this.frameId}-right-v3`;
        const cachedRightValue = localStorage.getItem(rightCacheKey);
        if (cachedRightValue) {
            const cachedRight = JSON.parse(cachedRightValue);
            if (cachedRight >= 0) {
                right = cachedRight;
            } else {
                localStorage.set(rightCacheKey, JSON.stringify(right));
            }
        }

        const bottomCacheKey = `meet-addon-${this.frameId}-bottom-v3`;
        const cachedBotomValue = localStorage.getItem(bottomCacheKey);
        if (cachedBotomValue) {
            const cachedBottom = JSON.parse(cachedBotomValue);
            if (cachedBottom >= 0) {
                bottom = cachedBottom;
            } else {
                localStorage.set(bottomCacheKey, JSON.stringify(bottom));
            }
        }

        this.state = {
            bottom,
            right,
        };

        observe(contextStore.layout, "tooltipRequested", () => {
            this.sendTooltipRequestMessage();
        });
        observe(configStore, "me", () => this.sendUserProfileChangeMessage());

        observe(contextStore.participantInfos, () => {

            const addonChanges = contextStore.participantInfos.filter(
                                    (p) => p.addonIdentifier === this.props.addonConfig.identifier);

            this.sendParticipantsMessage(addonChanges);
        });

        observe(contextStore, "meetingState", (change) => this.sendМeetingStateChange(change.newValue));

        observe(contextStore, "chromeState", (change) => this.sendChromeStateChange(change.newValue));

        observe(navigationStore, "activeRoute", (change) => this.sendActiveRouteChange(change.newValue));

        window.addEventListener("message", this.onAddonMessageReceived);

    }

    public componentDidUpdate() {

        if (this.dirtyMode) {
            this.sendAddonModeChangeMessage(this.mode);
        }
    }

    public render() {
        const { addonConfig } = this.props;

        this.dirtyMode = false;

        if (this.props.active) {
            this.dirtyMode = true;

            this.mode = AddonMode.FULLSCREEN;
            return this.renderNormalAddon();
        }

        if (addonConfig.inactiveMode === AddonInactiveMode.HIDDEN) {
            this.dirtyMode = true;
            this.mode = AddonMode.HIDDEN;
            return this.renderNormalAddon();
        }

        this.dirtyMode = true;
        this.mode = AddonMode.MINI;
        return this.renderMiniAddon();
    }

    private renderMiniAddon = () => {
        const {addonConfig} = this.props;
        const {classes} = this.props;

        return <div className={classes!.mini}
                    style={{
                        bottom: this.state.bottom,
                        right: this.state.right,
                    }}>
                {this.getFrame(addonConfig)}
        </div>;
    }

    private renderNormalAddon = () => {
        const { active, addonConfig, classes, offset } = this.props;
        const { chromeState } = this.props.contextStore!;

        const chromeAddon = `calc(100% - ${offset}px)`;
        const fullscreenAddon = `100%`;

        return <div style={{ height: chromeState === "fullscreen" ? fullscreenAddon : chromeAddon }}
                    hidden={ !active }
                    className={classes!.addon}>
                {this.getFrame(addonConfig)}
        </div>;
    }

    private getFrame = (addonConfig: AddonRuntimeInfo) => {

        const url = addonConfig.host.url;

        return React.createElement(
            "iframe",
            {
                id: this.frameId,
                onLoad: this.onLoad,
                src: url,
                style: {
                    border: "none",
                    height: "100%",
                    padding: 0,
                    width: "100%",
                },
            },
        );
    }

    private onLoad = () => {
        this.loaded = true;
        if (this.delayedMessages.length > 0) {
            const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;
            this.delayedMessages.forEach(msg => {
                if (iframe.contentWindow) {
                    this.sendMessage(msg);
                }
            });

            this.delayedMessages = [];
        }
    }

    private getOriginHost = () => {
        const addonHostUrl = new URL(this.props.addonConfig.host.url);
        let addonHost = `${addonHostUrl.protocol}//${addonHostUrl.hostname}`;

        if (addonHostUrl.port && addonHostUrl.port !== "80") {
            addonHost += `:${addonHostUrl.port}`;
        }

        return addonHost;
    }

    /**
     * Handler which is invoked every time host gets a message sent from some of the addons
     *
     * @private
     * @memberof EverestAddonElement
     */
    private onAddonMessageReceived = (message: MessageEvent) => {

        const { setChromeState } = this.props.contextStore!;

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if ( !message || !iframe || message.source !== iframe.contentWindow || !message.data) {
            // TODO:  m1 - report to TX?
            return;
        }

        if (message.origin !== this.getOriginHost()) {
            // TODO:  m1 - report to TX that someone is messaging the host from invalid domain
            return;
        }

        let addonMessage: AddonMessage;
        if (typeof message.data === "object") {
            addonMessage = message.data;
        } else {
            addonMessage = JSON.parse(message.data);
        }

        if (!addonMessage.type) {
            // TODO:  m1 - report maybe to TX that someone is messaging the host with invalid payload
            return;
        }

        switch (addonMessage.type) {
            case MessageType.READY:
                addonMessanger.sendInitMessage(this.props.addonConfig.identifier, this.mode);
                break;
            case MessageType.HOST_CHANGED:
                if (!addonMessage.payload) {
                    throw new Error("Host changed message missing payload");
                }

                const hostMessageAddon  = addonMessage as HostChangedMessage;

                setChromeState(hostMessageAddon.chromeState);
                this.mode = hostMessageAddon.addonMode;
                break;
            case MessageType.TOOLTIPS:
                if (!addonMessage.payload) {
                    throw new Error("Tooltip request message missing payload");
                }

                const requestId: number = JSON.parse(addonMessage.payload);
                this.props.contextStore!.proxyRequestTooltip(requestId);
                break;
            case MessageType.REPOSITION:
                    if (!addonMessage.payload) {
                        throw new Error("Tooltip request message missing payload");
                    }

                    const positionMessage = JSON.parse(addonMessage.payload) as {deltaX: number, deltaY: number};
                    this.setState((prevState) => ({
                    bottom: prevState.bottom - positionMessage.deltaY,
                    right: prevState.right - positionMessage.deltaX,
                }), () => {
                    if (this.state.right > 0) {
                        localStorage.setItem(`meet-addon-${this.frameId}-right-v2`, JSON.stringify(this.state.right));
                    }

                    if (this.state.bottom > 0) {
                        localStorage.setItem(`meet-addon-${this.frameId}-bottom-v2`, JSON.stringify(this.state.bottom));
                    }

                });
                    break;
            case MessageType.INIT_REQUESTED:
                contextStore.initAsync()
                    .then(() => {
                        addonMessanger.sendInitMessage(this.props.addonConfig.identifier, this.mode);
                    });
                break;
            case MessageType.BADGE_TEXT_UPDATE:
                const badgeUpdateMessage  = addonMessage as BadgeUpdateMessage;    
                panelStore.updateRuntimeContext(this.props.addonConfig.identifier, badgeUpdateMessage.text);
                break;
            case MessageType.HOST_SHELL_REQUEST:
                const hostShellRequestMessage  = addonMessage as HostShellRequestMessage;    
                if (hostShellRequestMessage.state === "show") {
                    contextStore.showShell();
                } else {
                    contextStore.hideShell();
                }
                
                break;
            case MessageType.HOST_ACTIVATION_REQUEST:
                window.location.hash = `/${PredefinedRoute.WORKSPACE}/${this.props.addonConfig.identifier}`;
                break;
            case MessageType.HOST_KEYBOARD_DOWN:
                const keydownMessage  = addonMessage as KeyDownMessage;    
                contextStore.setKeyDownMessage(keydownMessage);
                addonMessanger.sendKeyDownMessageToAddons(keydownMessage);
                break;
    
            default:
                // tslint:disable-next-line:no-console
                console.error("Unknown message type:" + addonMessage.type);
                break;
        }
    }


    private sendUserProfileChangeMessage = () => {

        const participantUserInfo = {
            avatarUrl: configStore.me!.avatarUrl,
            color: configStore.me!.color,
            displayName: this.getDisplayName(configStore.me!),
            theme: configStore.me!.theme,
        };

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message: AddonMessage = {
            payload: JSON.stringify(participantUserInfo),
            type: MessageType.USER_PROFILE,
        };

        this.sendMessage(JSON.stringify(message));
    }

    private sendМeetingStateChange = (state?: PredefinedMeetingState) => {

        if (!state) {
            return;
        }

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message = new AddonMessage(MessageType.MEET_STATE);
        message.payload = JSON.stringify(state);
        this.sendMessage(JSON.stringify(message));
    }

    private sendChromeStateChange = (state: ChromeState) => {

        const { me } = this.props.configStore!;

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message = new HostChangedMessage();
        message.chromeState = state;
        message.addonMode = this.mode;
        message.theme = me?.theme === "dark" ? AppTheme.DARK : AppTheme.LIGHT;

        this.sendMessage(JSON.stringify(message));
    }

    private sendActiveRouteChange = (activeRoute?: string) => {

        const { previousRoute } = this.props.navigationStore!;
        const { addonConfig } = this.props;

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        if ( activeRoute !== this.props.addonConfig.identifier) {

            if (previousRoute === addonConfig.identifier) {
                const addonInactiveMessage = new AddonInactivatedMessage();
                addonInactiveMessage.activeRoute = activeRoute || "";

                const inactiveMessage = JSON.stringify(addonInactiveMessage);
                iframe.contentWindow.postMessage(inactiveMessage, this.getOriginHost());
            }
            return;
        }

        const message = new AddonActivatedMessage();
        message.inactiveRoute = previousRoute || "";

        this.sendMessage(JSON.stringify(message));
    }

    private sendParticipantsMessage(participants: ParticipantInfo[]) {

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message = new AddonMessage(MessageType.PARTICIPANTS);
        message.payload = JSON.stringify(participants);

        this.sendMessage(JSON.stringify(message));
    }

    private sendTooltipRequestMessage = () => {

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message  = new AddonMessage(MessageType.TOOLTIPS);
        this.sendMessage(JSON.stringify(message));
    }

    private sendAddonModeChangeMessage = (mode: AddonMode) => {

        const { chromeState } = this.props.contextStore!;
        const { me } = this.props.configStore!;

        const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

        if (!iframe || !iframe.contentWindow) {
            // TODO:  m1 - report to TX?
            return;
        }

        const message = new HostChangedMessage();
        message.addonMode = mode;
        message.chromeState = chromeState;
        message.theme = me?.theme === "dark" ? AppTheme.DARK : AppTheme.LIGHT;
        this.sendMessage(JSON.stringify(message))
    }

    private getDisplayName = (user: UserSummaryInfo) => {
        const firstNameInitial = user.firstName.substr(0, 1).toUpperCase();

        if (user.familyName && !user.familyName.startsWith("(")) {
            const lastNameInitial = user.familyName.substr(0, 1).toUpperCase();
            return `${firstNameInitial} ${lastNameInitial}`;
        }

        return firstNameInitial;
    }

    private sendMessage = (message: string) => {
        // TODO:  m1 - report to TX this KPI?

        if (this.loaded) {
            const iframe = document.getElementById(this.frameId) as HTMLIFrameElement;

            if (!iframe || !iframe.contentWindow) {
                // TODO:  m1 - report to TX?
                return;
            }
            iframe.contentWindow.postMessage(message, this.getOriginHost());

        } else {
            this.delayedMessages.push(message);
        }
    }
}

export default withStyles(styles, {withTheme: true })(AddonLoader);
